/**
 * A lane or stage's state, as the same pill the games and Simulive tables use.
 *
 * Exists so the diagrams share that shape without learning about hexes: the hue
 * still comes from {@link toneTextColor}, so every box keeps exactly the colour
 * it had as a text label and only the shape changes.
 */

import { Box, useTheme, type SxProps, type Theme } from '@mui/material';

import StatusBadge from '@/shared/components/StatusBadge';

import { toneTextColor, type FlowTone } from './tokens';

interface FlowStatusBadgeProps {
  /** The state in the panel's own words — TM's `running`, a replay's `idle`. */
  label: string;
  tone: FlowTone;
  /** Fade the dot while a transition is in flight. */
  pulse?: boolean;
  /** Spacing against the lines above, applied to the wrapper. */
  sx?: SxProps<Theme>;
}

export default function FlowStatusBadge({
  label,
  tone,
  pulse = false,
  sx,
}: FlowStatusBadgeProps) {
  const theme = useTheme();

  // The wrapper is load-bearing, not spacing sugar: `StatusBadge` is
  // `inline-flex`, and as a direct child of FlowNodeCard's flex column it would
  // blockify and stretch to the full card width. Inside a block box it
  // shrink-wraps to its label everywhere.
  return (
    <Box sx={sx}>
      <StatusBadge
        label={label}
        color={toneTextColor(theme, tone)}
        pulse={pulse}
      />
    </Box>
  );
}
